import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, type PricingInfo } from "../../../api";
import { Badge } from "../../../components/ui";
import { fadeUp, stagger } from "../../../lib/motion";
import { fmtUsd } from "../../../lib/format";

export default function Models() {
  const [info, setInfo] = useState<PricingInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.getPricing().then(setInfo).catch((e) => setErr(e instanceof Error ? e.message : "failed"));
  }, []);

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <motion.div variants={fadeUp}>
        <div className="font-serif italic text-ink-soft">admin · models &amp; pricing</div>
        <h2 className="display text-4xl md:text-5xl mt-2">Inference stack.</h2>
        <p className="mt-3 text-ink-soft max-w-xl">
          Active model IDs and per-token rates. Changes require an env-var redeploy today.
        </p>
      </motion.div>

      {err && <div className="text-terracotta text-sm mt-4">{err}</div>}
      {!info && !err && <div className="text-ink-faint mt-4">loading…</div>}

      {info && (
        <>
          {/* Active models */}
          <motion.div variants={fadeUp} className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "Tutor", id: info.active_models.tutor },
              { label: "Signal extractor", id: info.active_models.signal_extractor },
              { label: "Planner", id: info.active_models.planner },
            ].map((m) => (
              <div key={m.label} className="card-ink p-5">
                <div className="eyebrow text-paper/40">{m.label}</div>
                <div className="font-mono text-sm mt-2 break-all text-paper">{m.id}</div>
              </div>
            ))}
          </motion.div>

          {/* System limits */}
          <motion.div variants={fadeUp} className="mt-6 card">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs text-ink-faint uppercase tracking-[0.14em]">global message limit</span>
                <div className="font-serif text-3xl mt-1">
                  {info.msg_limit.toLocaleString()}
                  <span className="text-ink-faint text-lg"> / user / day</span>
                </div>
              </div>
              <Badge tone="neutral">env · MSG_LIMIT</Badge>
            </div>
          </motion.div>

          {/* Pricing rates */}
          <motion.div variants={fadeUp} className="mt-6 card">
            <div className="mb-4">
              <span className="text-xs text-ink-faint uppercase tracking-[0.14em]">pricing rates</span>
              <h3 className="font-serif text-2xl mt-1">Per-million-token cost</h3>
            </div>
            {info.rates.length === 0 ? (
              <p className="text-ink-faint text-sm">OpenRouter returns pricing on each call — no static table.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(42,36,26,0.12)]">
                    <th className="text-left py-2 eyebrow text-[0.65rem]">Model IDs</th>
                    <th className="text-right py-2 eyebrow text-[0.65rem]">Input $/1M</th>
                    <th className="text-right py-2 eyebrow text-[0.65rem]">Output $/1M</th>
                  </tr>
                </thead>
                <tbody>
                  {info.rates.map((r, i) => (
                    <tr key={i} className="border-b border-[rgba(42,36,26,0.06)]">
                      <td className="py-2 font-mono text-xs">{r.model_ids.join(", ")}</td>
                      <td className="py-2 text-right font-mono">{fmtUsd(r.input_usd_per_1m)}</td>
                      <td className="py-2 text-right font-mono">{fmtUsd(r.output_usd_per_1m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-6 card">
            <div className="text-xs text-ink-faint uppercase tracking-[0.14em] mb-2">editing</div>
            <h3 className="font-serif text-xl mb-3">How to change a model</h3>
            <ol className="text-sm text-ink-soft space-y-2 leading-relaxed list-decimal list-inside">
              <li>Edit the model env var in <code className="text-xs px-1.5 py-0.5 bg-ink-card text-paper rounded">infra/template.yaml</code></li>
              <li>Run <code className="text-xs px-1.5 py-0.5 bg-ink-card text-paper rounded">sam deploy</code> to ship</li>
              <li>New requests hit the new model immediately</li>
            </ol>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
